import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Ticket, Zap, Gift, PartyPopper, TrendingUp, 
  ArrowRight, Plus, Clock, CheckCircle, XCircle 
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminMarketing = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOverview();
  }, []);

  const loadOverview = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/marketing/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverview(response.data);
    } catch (error) {
      console.error('Error loading marketing overview:', error);
      toast.error('Failed to load marketing data');
    } finally {
      setLoading(false);
    }
  };

  const marketingModules = [
    {
      id: 'coupons',
      title: 'Discount Coupons',
      description: 'Create percentage, flat, or free shipping discount codes',
      icon: Ticket,
      color: 'bg-green-500',
      link: '/admin/coupons',
      activeKey: 'coupons',
      features: ['Percentage discounts', 'Flat discounts', 'Free shipping', 'Usage limits', 'Category specific']
    },
    {
      id: 'flash-sales',
      title: 'Flash Sales',
      description: 'Time-limited sales with countdown timers',
      icon: Zap,
      color: 'bg-orange-500',
      link: '/admin/flash-sales',
      activeKey: 'flash_sales',
      features: ['Countdown timer', 'Limited stock', 'Homepage display', 'Priority sorting']
    },
    {
      id: 'bundle-offers',
      title: 'Bundle Offers',
      description: 'Buy X Get Y deals and combo discounts',
      icon: Gift,
      color: 'bg-purple-500',
      link: '/admin/bundle-offers',
      activeKey: 'bundle_offers',
      features: ['Buy 2 Get 1', 'Buy 2 Get 3', 'Combo discounts', 'Category based']
    },
    {
      id: 'festival-campaigns',
      title: 'Festival Campaigns',
      description: 'Seasonal promotional banners with animations',
      icon: PartyPopper,
      color: 'bg-pink-500',
      link: '/admin/festival-campaigns',
      activeKey: 'campaigns',
      features: ['Custom themes', 'Animations', 'Discount codes', 'All pages display']
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-marketing">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-light text-charcoal">Marketing Tools</h1>
          <p className="text-charcoal-light mt-1">Manage coupons, flash sales, bundles, and campaigns</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-charcoal/10 p-4">
          <div className="flex items-center justify-between mb-2">
            <Ticket className="w-6 h-6 text-green-500" />
            <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
              {overview?.active?.coupons || 0} active
            </span>
          </div>
          <p className="text-2xl font-heading font-light text-charcoal">{overview?.total?.coupons || 0}</p>
          <p className="text-xs text-charcoal-light">Total Coupons</p>
        </div>
        <div className="bg-white border border-charcoal/10 p-4">
          <div className="flex items-center justify-between mb-2">
            <Zap className="w-6 h-6 text-orange-500" />
            <span className="text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
              {overview?.active?.flash_sales || 0} live
            </span>
          </div>
          <p className="text-2xl font-heading font-light text-charcoal">{overview?.total?.flash_sales || 0}</p>
          <p className="text-xs text-charcoal-light">Flash Sales</p>
        </div>
        <div className="bg-white border border-charcoal/10 p-4">
          <div className="flex items-center justify-between mb-2">
            <Gift className="w-6 h-6 text-purple-500" />
            <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
              {overview?.active?.bundle_offers || 0} active
            </span>
          </div>
          <p className="text-2xl font-heading font-light text-charcoal">{overview?.total?.bundle_offers || 0}</p>
          <p className="text-xs text-charcoal-light">Bundle Offers</p>
        </div>
        <div className="bg-white border border-charcoal/10 p-4">
          <div className="flex items-center justify-between mb-2">
            <PartyPopper className="w-6 h-6 text-pink-500" />
            <span className="text-xs text-pink-600 bg-pink-50 px-2 py-0.5 rounded-full">
              {overview?.active?.campaigns || 0} live
            </span>
          </div>
          <p className="text-2xl font-heading font-light text-charcoal">{overview?.total?.campaigns || 0}</p>
          <p className="text-xs text-charcoal-light">Campaigns</p>
        </div>
      </div>

      {/* Marketing Modules */}
      <div className="grid md:grid-cols-2 gap-6">
        {marketingModules.map((module) => (
          <div 
            key={module.id}
            className="bg-white border border-charcoal/10 p-6 hover:border-gold/30 transition-all group"
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 ${module.color} flex items-center justify-center`}>
                <module.icon className="w-6 h-6 text-white" />
              </div>
              <Link
                to={module.link}
                className="flex items-center gap-1 text-sm text-gold opacity-0 group-hover:opacity-100 transition-opacity"
              >
                Manage <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            <h3 className="font-heading text-lg text-charcoal mb-1">{module.title}</h3>
            <p className="text-sm text-charcoal-light mb-4">{module.description}</p>
            
            <div className="flex flex-wrap gap-2 mb-4">
              {module.features.map((feature, idx) => (
                <span 
                  key={idx}
                  className="text-xs bg-beige/50 text-charcoal-light px-2 py-1"
                >
                  {feature}
                </span>
              ))}
            </div>
            
            <div className="flex items-center justify-between pt-4 border-t border-charcoal/5">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1 text-sm text-charcoal-light">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  {overview?.active?.[module.activeKey] || 0} Active
                </span>
                <span className="flex items-center gap-1 text-sm text-charcoal-light">
                  <Clock className="w-4 h-4 text-charcoal-light" />
                  {(overview?.total?.[module.activeKey] || 0) - (overview?.active?.[module.activeKey] || 0)} Inactive
                </span>
              </div>
              <Link
                to={module.link}
                className="flex items-center gap-1 px-3 py-1.5 bg-gold text-white text-xs hover:bg-gold-dark transition-colors"
              >
                <Plus className="w-3 h-3" /> Create
              </Link>
            </div>
          </div>
        ))}
      </div>

      {/* Top Performing Coupons */}
      {overview?.top_coupons?.length > 0 && (
        <div className="bg-white border border-charcoal/10">
          <div className="p-4 border-b border-charcoal/10 flex items-center justify-between">
            <h2 className="font-heading text-lg text-charcoal flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-gold" />
              Top Performing Coupons
            </h2>
            <Link to="/admin/coupons" className="text-sm text-gold hover:underline">
              View All
            </Link>
          </div>
          <div className="divide-y divide-charcoal/5">
            {overview.top_coupons.map((coupon) => (
              <div key={coupon.id} className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-mono text-sm font-medium text-charcoal">{coupon.code}</p>
                  <p className="text-xs text-charcoal-light">
                    {coupon.type === 'percentage' && `${coupon.value}% off`}
                    {coupon.type === 'flat' && `₹${coupon.value} off`}
                    {coupon.type === 'free_shipping' && 'Free Shipping'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-heading text-gold">{coupon.used_count || 0}</p>
                  <p className="text-xs text-charcoal-light">times used</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMarketing;
